import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, DownloadSimple, Folder, FolderPlus, UploadSimple, Trash, MagnifyingGlass, House, CaretRight, PencilSimple, Eye, X, PaperPlaneTilt } from '@phosphor-icons/react';
import { fmtDateFR } from '../../utils/dates';
import DropZone from '../DropZone';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { key: 'devis', label: 'Devis', color: '#1976D2' },
  { key: 'facture', label: 'Factures', color: '#2E7D32' },
  { key: 'contrat', label: 'Contrats', color: '#7B1FA2' },
  { key: 'plan', label: 'Plans', color: '#EF6C00' },
  { key: 'rapport', label: 'Rapports', color: '#0097A7' },
  { key: 'autre', label: 'Autres', color: '#5E5F66' },
];
const getCatMeta = (k) => CATEGORIES.find(c => c.key === k) || CATEGORIES[5];

const DocCard = ({ doc, onDownload, onDelete, onPreview, onSendEmail, canDelete, canManage }) => {
  const meta = getCatMeta(doc.category);
  const ext = (doc.original_filename || doc.filename || '').split('.').pop().toLowerCase();
  const canPreview = ['pdf', 'png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext);
  return (
    <div data-testid={`project-doc-${doc.id}`} style={{ background: 'white', border: '1px solid #E2E2E6', borderRadius: '8px', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '12px', cursor: canPreview ? 'pointer' : 'default' }}
         onClick={(e) => { if (e.target.closest('button')) return; if (canPreview) onPreview(doc); }}>
      <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: `${meta.color}15`, color: meta.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontWeight: '700', fontSize: '0.72rem', textTransform: 'uppercase' }}>
        {ext ? ext.slice(0, 4) : <FileText size={18} weight="bold" />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: '600', color: '#1E2030', fontSize: '0.92rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {doc.title || doc.original_filename || doc.filename}
        </div>
        <div style={{ fontSize: '0.75rem', color: '#5E5F66', marginTop: '2px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ color: meta.color, fontWeight: '600' }}>{meta.label}</span>
          <span>|</span>
          <span>{fmtDateFR(doc.created_at)}</span>
          {doc.uploaded_by_name && <><span>|</span><span>par {doc.uploaded_by_name}</span></>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: '4px' }}>
        {canPreview && (
          <button onClick={() => onPreview(doc)} className="btn btn-secondary btn-sm" title="Apercu" data-testid={`doc-preview-${doc.id}`} style={{ padding: '5px 8px' }}><Eye size={14} /></button>
        )}
        <button onClick={() => onDownload(doc)} className="btn btn-secondary btn-sm" title="Telecharger" data-testid={`doc-download-${doc.id}`} style={{ padding: '5px 8px' }}><DownloadSimple size={14} /></button>
        {canManage && onSendEmail && (
          <button onClick={() => onSendEmail(doc)} className="btn btn-secondary btn-sm" title="Envoyer par email" data-testid={`doc-email-${doc.id}`} style={{ padding: '5px 8px' }}><PaperPlaneTilt size={14} /></button>
        )}
        {canDelete && (
          <button onClick={() => onDelete(doc)} className="btn btn-danger btn-sm" title="Supprimer" data-testid={`doc-delete-${doc.id}`} style={{ padding: '5px 8px' }}><Trash size={14} /></button>
        )}
      </div>
    </div>
  );
};

export const ProjectDocumentsViewer = ({ project, canManage = false }) => {
  const [docs, setDocs] = useState([]);
  const [folders, setFolders] = useState([]);
  const [breadcrumb, setBreadcrumb] = useState([]); // [{id,name}...]
  const [currentFolderId, setCurrentFolderId] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [uploads, setUploads] = useState({}); // {name: pct}
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameFolder, setRenameFolder] = useState(null); // {id, name}
  const [previewDoc, setPreviewDoc] = useState(null); // {doc, url}
  const [emailModal, setEmailModal] = useState(null); // {doc, email, subject, message, sending}
  const fileRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [d, f] = await Promise.all([
        axios.get(`${API_URL}/api/projects/${project.id}/documents`, { params: { folder_id: currentFolderId }, withCredentials: true }),
        axios.get(`${API_URL}/api/projects/${project.id}/folders`, { params: { parent_id: currentFolderId }, withCredentials: true }),
      ]);
      setDocs(d.data || []);
      setFolders(f.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, [project.id, currentFolderId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const navigateToFolder = (folder) => {
    setCurrentFolderId(folder.id);
    setBreadcrumb([...breadcrumb, { id: folder.id, name: folder.name }]);
  };
  const navigateTo = (idx) => {
    // idx = -1 for root
    if (idx < 0) { setCurrentFolderId(''); setBreadcrumb([]); return; }
    const sliced = breadcrumb.slice(0, idx + 1);
    setBreadcrumb(sliced);
    setCurrentFolderId(sliced[sliced.length - 1].id);
  };

  const handleDownload = async (doc) => {
    try {
      const res = await axios.get(`${API_URL}${doc.url}`, { withCredentials: true, responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = doc.original_filename || doc.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch { toast.error('Erreur telechargement'); }
  };

  const handlePreview = async (doc) => {
    try {
      const res = await axios.get(`${API_URL}${doc.url}`, { withCredentials: true, responseType: 'blob' });
      const ext = (doc.original_filename || doc.filename || '').split('.').pop().toLowerCase();
      const mimeMap = { pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif' };
      const blob = new Blob([res.data], { type: mimeMap[ext] || 'application/octet-stream' });
      const blobUrl = window.URL.createObjectURL(blob);
      setPreviewDoc({ doc, url: blobUrl, ext });
    } catch { toast.error('Erreur apercu'); }
  };

  const closePreview = () => {
    if (previewDoc?.url) window.URL.revokeObjectURL(previewDoc.url);
    setPreviewDoc(null);
  };

  const handleSendEmail = async () => {
    if (!emailModal || !emailModal.email || !emailModal.email.includes('@')) {
      toast.error('Adresse email invalide');
      return;
    }
    setEmailModal({ ...emailModal, sending: true });
    try {
      await axios.post(`${API_URL}/api/projects/${project.id}/documents/${emailModal.doc.id}/send-email`, {
        email: emailModal.email.trim(),
        subject: emailModal.subject,
        message: emailModal.message,
      }, { withCredentials: true });
      toast.success(`Document envoye a ${emailModal.email}`);
      setEmailModal(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi email');
      setEmailModal({ ...emailModal, sending: false });
    }
  };

  const uploadOne = async (file) => {
    if (file.size > 50 * 1024 * 1024) { toast.error(`${file.name} trop lourd (max 50 Mo)`); return false; }
    setUploads(u => ({ ...u, [file.name]: 0 }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', file.name);
      fd.append('category', 'autre');
      if (currentFolderId) fd.append('folder_id', currentFolderId);
      await axios.post(`${API_URL}/api/projects/${project.id}/documents`, fd, {
        withCredentials: true,
        onUploadProgress: (p) => {
          const pct = p.total ? Math.round((p.loaded / p.total) * 100) : 0;
          setUploads(u => ({ ...u, [file.name]: pct }));
        },
      });
      setUploads(u => { const c = { ...u }; delete c[file.name]; return c; });
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || `Erreur upload ${file.name}`);
      setUploads(u => { const c = { ...u }; delete c[file.name]; return c; });
      return false;
    }
  };

  const handleUploadMany = async (files) => {
    if (!files?.length) return;
    let ok = 0;
    for (const f of files) {
      const r = await uploadOne(f);
      if (r) ok++;
    }
    if (ok) toast.success(`${ok} document(s) ajoute(s)`);
    fetchData();
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Supprimer "${doc.title || doc.filename}" ?`)) return;
    try {
      await axios.delete(`${API_URL}/api/projects/${project.id}/documents/${doc.id}`, { withCredentials: true });
      toast.success('Supprime');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      await axios.post(`${API_URL}/api/projects/${project.id}/folders`, { name, parent_id: currentFolderId || null }, { withCredentials: true });
      toast.success('Dossier cree');
      setNewFolderName('');
      setShowNewFolder(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleRenameFolder = async () => {
    if (!renameFolder || !renameFolder.name.trim()) return;
    try {
      await axios.put(`${API_URL}/api/projects/${project.id}/folders/${renameFolder.id}`, { name: renameFolder.name.trim() }, { withCredentials: true });
      toast.success('Dossier renomme');
      setRenameFolder(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleDeleteFolder = async (f) => {
    if (!window.confirm(`Supprimer le dossier "${f.name}" et tout son contenu ?`)) return;
    try {
      await axios.delete(`${API_URL}/api/projects/${project.id}/folders/${f.id}`, { withCredentials: true });
      toast.success('Dossier supprime');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const filteredDocs = docs.filter(d => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (d.title || '').toLowerCase().includes(s) || (d.original_filename || '').toLowerCase().includes(s);
  });
  const filteredFolders = folders.filter(f => !search || f.name.toLowerCase().includes(search.toLowerCase()));
  const uploading = Object.keys(uploads).length > 0;

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  return (
    <div data-testid="project-documents-viewer">
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px', fontSize: '0.88rem', flexWrap: 'wrap' }}>
        <button onClick={() => navigateTo(-1)} data-testid="breadcrumb-root" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#002FA7', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <House size={14} weight="bold" /> Racine
        </button>
        {breadcrumb.map((b, idx) => (
          <span key={b.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <CaretRight size={12} color="#9E9EA7" />
            <button onClick={() => navigateTo(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: idx === breadcrumb.length - 1 ? '#1A1A1E' : '#002FA7', fontWeight: idx === breadcrumb.length - 1 ? '700' : '500' }}>
              {b.name}
            </button>
          </span>
        ))}
      </div>

      {/* Toolbar */}
      <div className="card" style={{ padding: '12px 16px', marginBottom: '12px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 200px', position: 'relative' }}>
          <MagnifyingGlass size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9E9EA7', pointerEvents: 'none' }} />
          <input type="text" className="form-input" placeholder="Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="doc-search" style={{ paddingLeft: '38px' }} />
        </div>
        <div style={{ fontSize: '0.82rem', color: '#5E5F66' }}>{filteredFolders.length} dossier(s) | {filteredDocs.length} doc(s)</div>
        {canManage && (
          <>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowNewFolder(true)} data-testid="create-folder-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <FolderPlus size={14} weight="bold" /> Nouveau dossier
            </button>
            <input ref={fileRef} type="file" multiple onChange={(e) => handleUploadMany(Array.from(e.target.files || []))} style={{ display: 'none' }} data-testid="doc-upload-input" />
            <button className="btn btn-primary btn-sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="doc-upload-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <UploadSimple size={14} weight="bold" /> Ajouter
            </button>
          </>
        )}
      </div>

      {/* DropZone */}
      {canManage && (
        <div style={{ marginBottom: '12px' }}>
          <DropZone onFiles={handleUploadMany} multiple allowFolders testId="docs-dropzone"
                    label={uploading ? 'Upload en cours...' : 'Glissez-déposez vos fichiers ou un dossier complet'}
                    hint="50 Mo max par fichier" disabled={uploading} compact />
        </div>
      )}

      {/* Upload progress */}
      {uploading && (
        <div className="card" style={{ padding: '12px 14px', marginBottom: '12px' }} data-testid="upload-progress-panel">
          <div style={{ fontSize: '0.82rem', fontWeight: '600', marginBottom: '8px', color: '#5E5F66' }}>{Object.keys(uploads).length} fichier(s) en cours d'upload</div>
          {Object.entries(uploads).map(([name, pct]) => (
            <div key={name} style={{ marginBottom: '6px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', marginBottom: '3px' }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '280px' }}>{name}</span>
                <strong style={{ color: '#002FA7' }}>{pct}%</strong>
              </div>
              <div style={{ height: '4px', background: '#F4F5F7', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: '#002FA7', transition: 'width 0.2s' }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Folders */}
      {filteredFolders.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '10px', marginBottom: '16px' }}>
          {filteredFolders.map(f => (
            <div key={f.id} data-testid={`folder-${f.id}`} className="card" style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
                 onClick={() => navigateToFolder(f)}>
              <Folder size={28} color="#EF6C00" weight="fill" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: '600', fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                <div style={{ fontSize: '0.72rem', color: '#5E5F66' }}>{f.docs_count || 0} doc(s) · {f.subfolders_count || 0} dossier(s)</div>
              </div>
              {canManage && (
                <div style={{ display: 'flex', gap: '4px' }} onClick={(e) => e.stopPropagation()}>
                  <button className="btn btn-secondary btn-sm" onClick={() => setRenameFolder({ id: f.id, name: f.name })} title="Renommer" data-testid={`rename-folder-${f.id}`} style={{ padding: '4px 6px' }}><PencilSimple size={12} /></button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDeleteFolder(f)} title="Supprimer" data-testid={`delete-folder-${f.id}`} style={{ padding: '4px 6px' }}><Trash size={12} /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Documents */}
      {filteredDocs.length === 0 && filteredFolders.length === 0 ? (
        <div className="card" style={{ padding: '40px', textAlign: 'center', color: '#5E5F66' }}>
          <Folder size={42} style={{ opacity: 0.3, marginBottom: '8px' }} />
          <p style={{ margin: 0, fontSize: '0.9rem' }}>Ce dossier est vide</p>
        </div>
      ) : filteredDocs.length > 0 && (
        <div style={{ display: 'grid', gap: '8px' }}>
          {filteredDocs.map(d => <DocCard key={d.id} doc={d} onDownload={handleDownload} onDelete={handleDelete} onPreview={handlePreview}
                                          onSendEmail={(doc) => setEmailModal({ doc, email: '', subject: '', message: '', sending: false })}
                                          canDelete={canManage} canManage={canManage} />)}
        </div>
      )}

      {/* PDF / Image preview modal */}
      {previewDoc && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '20px' }}
             onClick={closePreview}>
          <div style={{ background: 'white', borderRadius: '10px', width: '100%', maxWidth: '1200px', height: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
               onClick={(e) => e.stopPropagation()} data-testid="doc-preview-modal">
            <div style={{ padding: '12px 18px', display: 'flex', alignItems: 'center', gap: '12px', borderBottom: '1px solid #E2E2E6' }}>
              <FileText size={20} color="#002FA7" weight="bold" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: '700', fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {previewDoc.doc.title || previewDoc.doc.original_filename}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#5E5F66' }}>{previewDoc.doc.original_filename}</div>
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => handleDownload(previewDoc.doc)}><DownloadSimple size={14} /> Télécharger</button>
              {canManage && (
                <button className="btn btn-primary btn-sm" data-testid="send-email-btn"
                        onClick={() => setEmailModal({ doc: previewDoc.doc, email: '', subject: '', message: '', sending: false })}>
                  <PaperPlaneTilt size={14} /> Envoyer par email
                </button>
              )}
              <button className="btn btn-secondary btn-sm" onClick={closePreview} data-testid="close-preview-btn"><X size={14} /></button>
            </div>
            <div style={{ flex: 1, background: '#F4F5F7', overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {previewDoc.ext === 'pdf' ? (
                <iframe title="preview" src={previewDoc.url} style={{ width: '100%', height: '100%', border: 'none' }} />
              ) : (
                <img src={previewDoc.url} alt={previewDoc.doc.title} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* New folder modal */}
      {showNewFolder && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '8px', width: '100%', maxWidth: '400px', padding: '22px' }}>
            <h3 style={{ margin: '0 0 12px 0' }}>Nouveau dossier</h3>
            <input className="form-input" autoFocus value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} placeholder="Nom du dossier" data-testid="new-folder-input"
                   onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()} />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '14px' }}>
              <button className="btn btn-secondary" onClick={() => { setShowNewFolder(false); setNewFolderName(''); }}>Annuler</button>
              <button className="btn btn-primary" onClick={handleCreateFolder} data-testid="create-folder-submit">Créer</button>
            </div>
          </div>
        </div>
      )}

      {renameFolder && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '8px', width: '100%', maxWidth: '400px', padding: '22px' }}>
            <h3 style={{ margin: '0 0 12px 0' }}>Renommer le dossier</h3>
            <input className="form-input" autoFocus value={renameFolder.name} onChange={(e) => setRenameFolder({ ...renameFolder, name: e.target.value })}
                   onKeyDown={(e) => e.key === 'Enter' && handleRenameFolder()} />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '14px' }}>
              <button className="btn btn-secondary" onClick={() => setRenameFolder(null)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleRenameFolder}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      {emailModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, padding: '16px' }}>
          <div style={{ background: 'white', borderRadius: '10px', width: '100%', maxWidth: '520px', padding: '24px' }} data-testid="email-modal">
            <h3 style={{ margin: '0 0 14px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <PaperPlaneTilt size={20} color="#002FA7" weight="fill" /> Envoyer par email
            </h3>
            <div style={{ padding: '10px 12px', background: '#F4F5F7', borderRadius: '6px', marginBottom: '14px', fontSize: '0.82rem' }}>
              <FileText size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
              <strong>{emailModal.doc.title || emailModal.doc.original_filename}</strong>
              <div style={{ color: '#5E5F66', marginTop: '2px' }}>{emailModal.doc.original_filename}</div>
            </div>
            <div className="form-group">
              <label className="form-label">Destinataire *</label>
              <input className="form-input" type="email" autoFocus required placeholder="client@example.fr"
                     value={emailModal.email} onChange={(e) => setEmailModal({ ...emailModal, email: e.target.value })}
                     data-testid="email-recipient-input" />
            </div>
            <div className="form-group">
              <label className="form-label">Objet (optionnel)</label>
              <input className="form-input" placeholder="Laissez vide pour un objet automatique"
                     value={emailModal.subject} onChange={(e) => setEmailModal({ ...emailModal, subject: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Message (optionnel)</label>
              <textarea className="form-input" rows="4" placeholder="Ajoutez un message personnel..."
                        value={emailModal.message} onChange={(e) => setEmailModal({ ...emailModal, message: e.target.value })} />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '6px' }}>
              <button className="btn btn-secondary" onClick={() => setEmailModal(null)} disabled={emailModal.sending}>Annuler</button>
              <button className="btn btn-primary" onClick={handleSendEmail} disabled={emailModal.sending || !emailModal.email} data-testid="send-email-submit">
                <PaperPlaneTilt size={14} /> {emailModal.sending ? 'Envoi...' : 'Envoyer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDocumentsViewer;
